import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Circle, FeatureGroup, MapContainer, TileLayer } from 'react-leaflet';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import { FeatureCollection, Polygon } from 'geojson';
import bbox from '@turf/bbox';

import { DrawingMode, InitialFocus, Map2Props } from '@Types';

// @ts-ignore
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
// @ts-ignore
import markerIcon from 'leaflet/dist/images/marker-icon.png';
// @ts-ignore
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import EditControl from '@Components/Map2/EditControl';

import styles from './Map2.module.scss';
import { DisplayFeatureCollection } from './DisplayFeatureCollection';
import { UK_BOUNDING_BOX } from '@Helpers/constants';

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  // The || just makes Next.js and Storybook happy
  iconUrl: markerIcon.src || markerIcon,
  iconRetinaUrl: markerIcon2x.src || markerIcon2x,
  shadowUrl: markerShadow.src || markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function getMeters(radius: number): number {
  return radius * 1609.344;
}

export const Map2 = ({
  boundingBox,
  center,
  circles,
  drawingMode,
  featureCollection,
  height,
  initialEditableFeatureCollection,
  initialFocus,
  markers,
  message,
  popupFunctions,
  refocus,
  setEditableFeatureCollection,
  zoom,
  onMarkerClick,
}: Map2Props) => {
  const map = useRef(null);
  const polygonDraw = useRef(null);
  const polygonGroup = useRef(null);

  const featureGroup = useRef(null);
  const circleLayer = useRef(null);

  const [lastRefocus, setLastRefocus] = useState(refocus);

  const focusMap = useCallback(() => {
    if (initialFocus !== InitialFocus.NONE && map && map.current !== null) {
      // Check if there layers otherwise getBounds going to fitBounds will explode
      if (initialFocus === InitialFocus.DATA) {
        let bboxFeatureCollection: FeatureCollection = {
          type: 'FeatureCollection',
          features: [],
        };
        if (featureCollection) {
          bboxFeatureCollection.features = bboxFeatureCollection.features.concat(
            featureCollection.features,
          );
        }
        if (initialEditableFeatureCollection) {
          bboxFeatureCollection.features = bboxFeatureCollection.features.concat(
            initialEditableFeatureCollection.features,
          );
        }
        if (circleLayer && circleLayer.current) {
          // @ts-ignore
          let circleBounds = circleLayer.current.getBounds();
          let northEastPoint = circleBounds.getNorthEast();
          let southWestPoint = circleBounds.getSouthWest();
          bboxFeatureCollection.features = bboxFeatureCollection.features.concat([
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [northEastPoint.lng, northEastPoint.lat],
              },
              properties: {},
            },
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [southWestPoint.lng, southWestPoint.lat],
              },
              properties: {},
            },
          ]);
        }
        let turfBbox = bbox(bboxFeatureCollection);

        // If we don't have any features in bboxFeatureCollection then turfBbox will be
        // [Infinity, Infinity, -Infinity, -Infinity]
        // and will break things when we call fitBounds on it.
        if (turfBbox[0] !== Infinity) {
          // @ts-ignore
          map.current.fitBounds([
            [turfBbox[1], turfBbox[0]],
            [turfBbox[3], turfBbox[2]],
          ]);
        }
      } else if (initialFocus == InitialFocus.UK_WIDE) {
        // @ts-ignore
        map.current.fitBounds(UK_BOUNDING_BOX);
      }
    }
  }, [featureCollection, initialEditableFeatureCollection, initialFocus, circleLayer]);

  useEffect(() => {
    if (boundingBox && map && map.current !== null) {
      // @ts-ignore
      map.current.fitBounds(boundingBox);
    }
  }, [boundingBox]);

  const setupMap = useCallback(
    async (leafletMap) => {
      if (map.current === null) {
        map.current = leafletMap;
        focusMap();
      }
    },
    [focusMap],
  );

  useEffect(() => {
    if (map && polygonGroup && polygonGroup.current) {
      if (drawingMode === DrawingMode.POLYGON) {
        // @ts-ignore
        map.current.addLayer(polygonGroup.current);

        if (polygonDraw.current === null) {
          //@ts-ignore
          // Auto select the polygon draw tool
          polygonDraw.current = new L.Draw.Polygon(map.current);
        }
        //@ts-ignore
        polygonDraw.current.enable();
      } else {
        if (polygonDraw.current !== null) {
          //@ts-ignore
          polygonDraw.current.disable();
        }
        // @ts-ignore
        // Hide the layer of editable features
        map.current.removeLayer(polygonGroup.current);
      }
    }
  }, [map, drawingMode, polygonGroup]);

  useEffect(() => {
    if (polygonGroup && polygonGroup.current && initialEditableFeatureCollection) {
      // This reads incredibly hacky and it is. But it means an already
      // existing polygon can be edited.
      initialEditableFeatureCollection.features.forEach((feature) => {
        if (feature.geometry.type === 'Polygon') {
          let geoJSONGroup = L.geoJSON(feature);
          geoJSONGroup.eachLayer((layer) => {
            // @ts-ignore
            polygonGroup.current.addLayer(layer);
          });
        } else if (feature.geometry.type === 'MultiPolygon') {
          feature.geometry.coordinates.forEach((polygonCoords) => {
            let polygon: Polygon = {
              type: 'Polygon',
              coordinates: polygonCoords,
            };
            var geoJSONGroup = L.geoJSON(polygon);
            geoJSONGroup.eachLayer((layer) => {
              // @ts-ignore
              polygonGroup.current.addLayer(layer);
            });
          });
        }
      });
      if (map && map.current && boundingBox) {
        // @ts-ignore
        map.current.fitBounds(boundingBox);
      }
    }
    // The rule dislikes polygonGroup.current, but without it the editable polygons don't load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEditableFeatureCollection, polygonGroup.current]);

  // While we need focusMap as a dep we don't want to trigger it unless last refocus has actually changed.
  useEffect(() => {
    if (lastRefocus !== refocus) {
      focusMap();
      setLastRefocus(refocus);
    }
  }, [focusMap, lastRefocus, refocus]);

  // @ts-ignore
  const onChange = (e) => {
    if (setEditableFeatureCollection && polygonGroup.current) {
      // @ts-ignore
      setEditableFeatureCollection(polygonGroup.current.toGeoJSON());
    }
  };

  // @ts-ignore
  const onStart = (e) => {
    if (polygonDraw.current !== null) {
      //@ts-ignore
      polygonDraw.current.disable();
    }
  };

  return (
    <MapContainer
      className={styles.mapContainer}
      center={center ? center : [0, 0]}
      style={{ height }}
      scrollWheelZoom={false}
      zoom={zoom || 13}
      whenCreated={setupMap}
    >
      <TileLayer
        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FeatureGroup ref={featureGroup}>
        {circles &&
          circles.map((circle, i) => {
            return (
              <Circle
                center={circle.center}
                key={i}
                radius={getMeters(circle.radius)}
                ref={circleLayer}
              />
            );
          })}
        {featureCollection && (
          <DisplayFeatureCollection
            featureCollection={featureCollection}
            markers={markers}
            popupFunctions={popupFunctions}
            onMarkerClick={onMarkerClick}
          />
        )}
      </FeatureGroup>
      <FeatureGroup ref={polygonGroup}>
        <EditControl
          position="topright"
          show={drawingMode === DrawingMode.POLYGON}
          onEdited={onChange}
          onCreated={onChange}
          onDeleted={onChange}
          onEditStart={onStart}
          onDeleteStart={onStart}
          draw={{
            circle: false,
            // @ts-ignore
            circlemarker: false,
            marker: false,
            polyline: false,
            rectangle: false,
          }}
        />
      </FeatureGroup>
      {message && message.visible && (
        <div className={styles.message}>
          <p>{message.content}</p>
        </div>
      )}
    </MapContainer>
  );
};
